import React, { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Dialog } from 'primereact/dialog';
import { MobXProviderContext } from "mobx-react";
import SearchAddress from "../../../components/general/SearchAddress";
import { extractLocationDetails } from "../../../utils/stringUtils";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { BusinessHomeProfile } from "../../../models/businessHomeProfile";
import { Toast } from "primereact/toast";
import { getErrorTitle, getToastMessageForSubscriptionLimitReached } from "../../../constants/constants";
import { RouterAddress } from "../../../constants/RouterAddress";
import {
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { useTour } from "../../../shared/tour/TourProvider";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

type HomeLimitDialogVariant = "free_usage_limit" | "subscription_required" | "need_more_home_capacity";

type AddHomeModalProps = {
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  addFlag: boolean;
  setAddFlag: (flag: boolean) => void;
};

const AddHomeModal: FC<AddHomeModalProps> = ({
  isOpen,
  toggleModal,
  addFlag,
  setAddFlag
}) => {
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const { completeStep, isStepActive } = useTour();
  const authExperience = useAuthExperience();
  const facilityTypeEditable = authExperience.providerSignup?.editable !== false;
  const defaultFacilityTypeValue = facilityTypeEditable
    ? ""
    : (authExperience.providerSignup?.defaultFacilityType || "afh");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [addressLine1, setAddressLine1] = useState<string>("");
  const [addressLine2, setAddressLine2] = useState<string>("");
  const [formattedAddress, setFormattedAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [latitude, setLatitude] = useState<string>("");
  const [longitude, setLongitude] = useState<string>("");
  const [showHomeLimitDialog, setShowHomeLimitDialog] = useState(false);
  const [currentHomeLimit, setCurrentHomeLimit] = useState<number>(0);
  const [homeLimitDialogVariant, setHomeLimitDialogVariant] = useState<HomeLimitDialogVariant>("subscription_required");
  const toast = React.useRef<Toast>(null);

  const defaultValues = {
    businessName: '',
    facilityType: defaultFacilityTypeValue,
    businessPhoneNumber: '',
    businessFaxNumber: '',
    businessAddress: '',
  }

  const { control, formState: { errors }, handleSubmit, reset, setValue } = useForm({ defaultValues });
  useEffect(() => {
    setValue("facilityType", defaultFacilityTypeValue);
  }, [defaultFacilityTypeValue, setValue]);

  const getHomeLimitDialogContent = () => {
    if (homeLimitDialogVariant === "free_usage_limit") {
      return {
        title: "Facility Limit Reached",
        subtitle: `Free usage includes up to ${currentHomeLimit > 0 ? currentHomeLimit : 1} facility${currentHomeLimit === 1 || currentHomeLimit === 0 ? "" : "ies"}.`,
        detail: "You have reached the free facility limit. Upgrade to add another facility.",
        ctaLabel: "View Subscription",
      };
    }

    if (homeLimitDialogVariant === "need_more_home_capacity") {
      return {
        title: "More Facility Access Needed",
        subtitle: "Your current plan has reached its facility limit.",
        detail: "Your subscription is active, but all included facility slots are already in use. Buy more home capacity to continue.",
        ctaLabel: "Manage Billing",
      };
    }

    return {
      title: "Subscription Required",
      subtitle: "Start a subscription to add more facilities.",
      detail: "A subscription is required before you can add more than the included free facility access.",
      ctaLabel: "View Subscription",
    };
  };

  const onSubmit = async (data: any) => {

    if (!formattedAddress || formattedAddress.trim() === "") {
      setErrorMessage("Please provide a complete valid address.");
      return;
    }

    let businessHomeData = new BusinessHomeProfile();
    businessHomeData.isWebsiteActive = false;
    businessHomeData.businessName = data.businessName;
    businessHomeData.facilityType = String(data.facilityType || defaultFacilityTypeValue).trim().toLowerCase();
    businessHomeData.businessPhoneNumber = data.businessPhoneNumber;
    businessHomeData.businessFaxNumber = String(data.businessFaxNumber || "").trim();

    businessHomeData.businessAddressLine1 = addressLine1;
    businessHomeData.businessAddressLine2 = addressLine2;
    businessHomeData.formattedAddress = formattedAddress;
    businessHomeData.city = city;
    businessHomeData.state = state;
    businessHomeData.country = country;
    businessHomeData.zipcode = postalCode;
    businessHomeData.latitude = latitude.toString();
    businessHomeData.longitude = longitude.toString();

    setLoading(true);
    try {
      setShowHomeLimitDialog(false);
      await backendAPIStore.init();
      const response = await backendAPIStore.addHome(businessHomeData);
      if (response) {
        if (isStepActive("save-home")) {
          completeStep("save-home");
        }
        reset();
        resetAddressFields();
        toggleModal(false);
        setAddFlag(!addFlag);
        setErrorMessage('');
      }
    } catch (error: any) {
      console.error("Error adding home:", error);
      setErrorMessage(error.message);
      const subscriptionLimitDetails = error?.details?.details || error?.details || {};
      if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        const fallbackHomeLimit = Number(
          subscriptionLimitDetails?.allowedHomes
          || subscriptionLimitDetails?.purchasedAllowedHomes
          || 1
        );
        setCurrentHomeLimit(fallbackHomeLimit);
        try {
          const billingDetails = await backendAPIStore.getSubscription(authStore.getUserId());
          const paidSubscriptionActive = billingDetails?.paidSubscriptionActive === true;
          const freeTrialUsed = billingDetails?.freeTrialUsed === true;
          const allowedHomes = Number(billingDetails?.allowedHomes || 0);
          const purchasedAllowedHomes = Number(billingDetails?.purchasedAllowedHomes || 0);
          const effectiveHomeLimit = Math.max(
            fallbackHomeLimit,
            paidSubscriptionActive ? allowedHomes : Math.max(allowedHomes, purchasedAllowedHomes),
            purchasedAllowedHomes,
          );

          setCurrentHomeLimit(effectiveHomeLimit > 0 ? effectiveHomeLimit : fallbackHomeLimit);

          if (paidSubscriptionActive || purchasedAllowedHomes > 0) {
            setHomeLimitDialogVariant("need_more_home_capacity");
          } else if (!freeTrialUsed) {
            setHomeLimitDialogVariant("free_usage_limit");
          } else {
            setHomeLimitDialogVariant("subscription_required");
          }
        } catch {
          setHomeLimitDialogVariant("subscription_required");
        }
        setShowHomeLimitDialog(true);
        setErrorMessage('');
      } else {
        toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const resetAddressFields = () => {
    setAddressLine1("");
    setAddressLine2("");
    setFormattedAddress("");
    setCity("");
    setState("");
    setCountry("");
    setPostalCode("");
    setLatitude("");
    setLongitude("");
  }

  const openSubscriptionBilling = () => {
    toggleModal(false);
    window.location.href = `${RouterAddress.BILLING}?tab=subscription`;
  };

  const handleAddressChange = (e) => {
    const place = e.target?.place;
    if (place) {
      const details = extractLocationDetails(place);
      setAddressLine1(details.addressLine1 || "");
      setAddressLine2(details.addressLine2 || "");
      setFormattedAddress(place.formatted_address || "");
      setCity(details.city || "");
      setState(details.state || "");
      setCountry(details.country || "");
      setPostalCode(details.postalCode || "");
      setLatitude(place.geometry?.location?.lat() || "");
      setLongitude(place.geometry?.location?.lng() || "");
      setValue("businessAddress", place.formatted_address || "", { shouldValidate: true });
    }
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const headerElement = renderSurfaceHeader(
    "Add Home",
    authExperience.providerHome.addHomeSubtitle,
    "pi pi-home",
  );

  const footerContent = renderDialogFooter({
    errorMessage,
    loading,
    onCancel: () => {
      setShowHomeLimitDialog(false);
      toggleModal(false);
    },
    onSubmit: handleSubmit(onSubmit),
    submitLabel: "Save Home",
    submitButtonProps: {
      "data-tour": "provider-save-home",
    },
  });
  const homeLimitDialogContent = getHomeLimitDialogContent();

  return (
    <>
      <Dialog visible={isOpen} modal header={headerElement} footer={footerContent} style={appDialogStyle}
        headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
        onHide={() => { if (!isOpen) return; setShowHomeLimitDialog(false); toggleModal(false); }}>
        <Toast ref={toast} />
        <form>
          <div style={appFormSectionStyle}>
            <div className="field">
              <span className="p-float-label">
                <Controller name="businessName" control={control} rules={{
                  required: authExperience.providerHome.homeNameRequiredMessage,
                  maxLength: { value: 30, message: 'Home name must be at most 30 characters long.' }
                }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} maxLength={30} className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
                <label htmlFor="businessName" className={classNames({ 'p-error': errors.businessName })}>{authExperience.providerHome.homeNameLabel}</label>
              </span>
              {getFormErrorMessage('businessName')}
            </div>
            {facilityTypeEditable ? (
              <div className="field">
                <label className="mb-2 block">{authExperience.providerHome.facilityTypeLabel}</label>
                <Controller
                  name="facilityType"
                  control={control}
                  rules={{ required: "Please select a facility type." }}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      id={field.name}
                      value={field.value}
                      options={(authExperience.facilityTypeOptions || []).filter((option) => String(option?.value || "").trim().toLowerCase() !== "custom")}
                      optionLabel="label"
                      optionValue="value"
                      onChange={(event) => field.onChange(event.value)}
                      placeholder="Select facility type"
                      className={classNames({ 'p-invalid': fieldState.invalid })}
                    />
                  )}
                />
                {getFormErrorMessage('facilityType')}
              </div>
            ) : null}
            <div className="field">
              <label className="mb-2 block">{authExperience.providerHome.homeAddressLabel}</label>
              <span className="p-float-label">
                <Controller name="businessAddress" control={control} rules={{ required: 'Please select your full address from the Google suggestions.' }} render={({ field, fieldState }) => (
                  <SearchAddress id={field.name} {...field}
                    onChange={handleAddressChange}
                    className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
              </span>
              {getFormErrorMessage('businessAddress')}
            </div>
            <div className="field mb-0">
              <span className="p-float-label">
                <Controller name="businessPhoneNumber" control={control} rules={{
                  required: 'Phone number is required.',
                  pattern: { value: /^[0-9]*$/, message: 'Phone number must be numeric.' },
                  maxLength: { value: 15, message: 'Phone number must be at most 15 characters long.' }
                }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
                <label htmlFor="businessPhoneNumber" className={classNames({ 'p-error': errors.businessPhoneNumber })}>Phone</label>
              </span>
              {getFormErrorMessage('businessPhoneNumber')}
            </div>
            <div className="field mb-0" style={{ marginTop: "1.5rem" }}>
              <span className="p-float-label">
                <Controller name="businessFaxNumber" control={control} rules={{
                  pattern: { value: /^[0-9+()\-.\s]*$/, message: 'Enter a valid fax number.' },
                  maxLength: { value: 30, message: 'Fax number must be at most 30 characters long.' }
                }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
                <label htmlFor="businessFaxNumber" className={classNames({ 'p-error': errors.businessFaxNumber })}>Fax (optional)</label>
              </span>
              {getFormErrorMessage('businessFaxNumber')}
            </div>
          </div>
        </form>
      </Dialog>
      <Dialog
        visible={showHomeLimitDialog}
        modal
        header={renderSurfaceHeader(homeLimitDialogContent.title, homeLimitDialogContent.subtitle, "pi pi-home")}
        style={appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setShowHomeLimitDialog(false)}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ color: "#596F80", lineHeight: 1.7 }}>
            {homeLimitDialogContent.detail}
          </div>
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Close"
              severity="secondary"
              text
              rounded
              onClick={() => setShowHomeLimitDialog(false)}
            />
            <Button
              type="button"
              label={homeLimitDialogContent.ctaLabel}
              severity="secondary"
              outlined
              rounded
              onClick={openSubscriptionBilling}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddHomeModal;
